import { EntityTags, ErrorCode, WRITE_SET_HEADER, type EntityTag } from '@signalvote/shared';
import { API_HEADERS } from './headers';

/**
 * One entry in the `stale` array returned by the API's 409 legal_acceptance_required
 * response. The client uses this to know which docs to render on the accept screen.
 */
export interface LegalStaleDoc {
  docType: string;         // "Eula" | "PrivacyPolicy" | "TermsOfService"
  requiredSequence: number;
}

/**
 * Thrown when the API returns 409 with `code: 'legal_acceptance_required'`. Callers
 * that need to distinguish the wall from a real error should branch on `err.code`
 * (or `err instanceof LegalAcceptanceRequiredError`) — never string-match the message,
 * which is human-readable prose that can drift.
 */
export class LegalAcceptanceRequiredError extends Error {
  readonly code = ErrorCode.legal_acceptance_required;
  readonly stale: LegalStaleDoc[];

  constructor(stale: LegalStaleDoc[], message?: string) {
    super(message ?? 'Legal acceptance required');
    this.name = 'LegalAcceptanceRequiredError';
    this.stale = stale;
  }
}

/**
 * A request the server answered with an error. Still an `Error` whose message is the server's own,
 * plus the HTTP `status` and the server's machine-readable `code` (the body's `code`), so a caller
 * can branch on `code` — never on the prose.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** When the server said to come back later (a 429), how long it asked us to wait. */
  readonly retryAfterSeconds: number | null;
  /** The whole refusal body, for the fields a refusal carries beyond its sentence and code. */
  readonly body: Record<string, unknown>;

  constructor(
    status: number,
    code: string | null,
    message: string,
    retryAfterSeconds: number | null = null,
    body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.body = body;
  }
}

/**
 * A refusal is `{ error: <sentence>, code: <code> }`; a body without a code (a 500 from the
 * exception middleware) carries its sentence in `message`.
 */
function errorCode(body: { code?: unknown } | null | undefined): string | null {
  return typeof body?.code === 'string' ? body.code : null;
}

/** A 409 body is the legal wall (owes a re-consent), not a conflict, when it carries this code and the stale list. */
export function isLegalWall(
  body: { code?: unknown; stale?: unknown } | null | undefined,
): body is { code: string; stale: LegalStaleDoc[]; message?: string } {
  return body?.code === ErrorCode.legal_acceptance_required && Array.isArray(body.stale);
}

const KNOWN_TAGS: ReadonlySet<string> = new Set(EntityTags);

/**
 * The entity types a response says its request wrote (`X-SV-Writes`), or null when it names no
 * write set, such as a read. A tag this build doesn't know is left out: no read here can name it,
 * so there is nothing of it to refresh.
 */
export function writesOf(response: Response): EntityTag[] | null {
  const header = response.headers.get(WRITE_SET_HEADER);
  if (header === null) return null;
  return header.split(',').map((tag) => tag.trim()).filter((tag): tag is EntityTag => KNOWN_TAGS.has(tag));
}

export interface ApiClientConfig {
  baseURL: string;
  getAuthToken?: () => string | null;
  /**
   * Renews the session after a 401 and resolves whether it worked. On true the request is replayed
   * once, reading the new token from `getAuthToken`; on false, a throw, or a replay that 401s again,
   * `onAuthError` fires. Requests that 401 together share one renewal.
   */
  refreshAuth?: () => Promise<boolean>;
  onAuthError?: () => void;
  onPaymentRequired?: (subscriptionStatus?: string) => void;
  /**
   * Fires when the API returns 409 legal_acceptance_required. The consumer typically
   * navigates the user to a "please accept the updated terms" screen. Distinct from
   * 409 LegalVersionMismatch (which the POST /accept handler shows as a toast).
   */
  onLegalAcceptanceRequired?: (stale: LegalStaleDoc[]) => void;
  /**
   * Fires on every response that names the entity types its request wrote (`X-SV-Writes`, on every
   * non-GET response, a refusal included), before the body is read or a refusal thrown. One cache
   * invalidates the reads of those types from here, so no call site lists keys.
   */
  onWrites?: (writes: EntityTag[]) => void;
  // Multi-tenancy header providers
  getSubsidiaryCode?: () => string | null;
  getCampaignId?: () => string | null;
}

export class ApiClient {
  private config!: ApiClientConfig;
  private isInitialized = false;
  private refreshing: Promise<boolean> | null = null;

  initialize(config: ApiClientConfig) {
    this.config = config;
    this.isInitialized = true;
  }

  getBaseURL(): string {
    if (!this.isInitialized) {
      throw new Error('ApiClient not initialized. Call initialize() first.');
    }
    return this.config.baseURL;
  }

  /**
   * Who is calling (the bearer token) and on whose behalf (subsidiary + campaign) — the headers
   * that identify a request, as opposed to the ones describing its body. Public because a few
   * payloads are fetched by something other than this client — React Native's `<Image>` loads an
   * authenticated image itself — and those must send the same headers rather than re-deriving them.
   *
   * A header whose value is missing is omitted entirely: the API reads presence, so an empty
   * `X-Campaign-Id` is a 400 rather than the honest "no campaign selected yet".
   */
  identityHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = this.config?.getAuthToken?.();
    const subsidiaryCode = this.config?.getSubsidiaryCode?.();
    const campaignId = this.config?.getCampaignId?.();

    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (subsidiaryCode) headers[API_HEADERS.SUBSIDIARY] = subsidiaryCode;
    if (campaignId) headers[API_HEADERS.CAMPAIGN_ID] = campaignId;

    return headers;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await this.fetchOk(endpoint, options);
    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }
    return await response.json();
  }

  /**
   * Calls an action whose success body is a file (PDF, CSV, zip). Resolves to the response so the caller reads the
   * bytes with `blob()` and the filename from `Content-Disposition`; a failure throws exactly as `request` does.
   */
  async file(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', endpoint: string, body?: unknown): Promise<Response> {
    return this.fetchOk(endpoint, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  }

  /**
   * Opens an action that sends server-sent events (`text/event-stream`). Resolves to the response once the headers
   * arrive, so the caller reads `body` as events land; a refusal throws exactly as `request` does. The same fetch as
   * every call, so auth and tenant headers ride along, which `EventSource` can't send.
   */
  async stream(endpoint: string): Promise<Response> {
    return this.fetchOk(endpoint, { method: 'GET' });
  }

  private async fetchOk(endpoint: string, options: RequestInit): Promise<Response> {
    try {
      const response = await this.fetchAuthed(endpoint, options);

      if (response.status === 401) {
        throw new ApiError(401, null, 'Authentication required');
      }

      if (response.status === 402) {
        const body = await response.json().catch(() => ({ message: 'Payment required' }));
        this.config.onPaymentRequired?.(body.subscriptionStatus);
        throw new ApiError(402, errorCode(body), body.error || body.message || 'Payment required');
      }

      if (response.status === 409) {
        // Only the legal wall gets the callback dispatch. LegalVersionMismatch (raised by
        // POST /accept when the sequence has drifted) and any other 409 fall through
        // to generic error handling so callers can show a normal toast.
        const body = await response.json().catch(() => ({ }));
        if (isLegalWall(body)) {
          this.config.onLegalAcceptanceRequired?.(body.stale);
          throw new LegalAcceptanceRequiredError(body.stale, body.message);
        }
        throw new ApiError(409, errorCode(body), body?.error || body?.message || 'Conflict', null, body ?? {});
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new ApiError(
          response.status,
          errorCode(error),
          error.error || error.message || `HTTP ${response.status}`,
          typeof error.retryAfterSeconds === 'number' ? error.retryAfterSeconds : null,
          error ?? {},
        );
      }

      return response;
    } catch (error: unknown) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Cannot connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  // Return raw response for cases where caller needs headers/status
  async requestRaw(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const response = await this.fetchAuthed(endpoint, options);

    if (response.status === 402) {
      this.config.onPaymentRequired?.();
    }

    if (response.status === 409) {
      // Sniff the body for legal_acceptance_required without consuming it — clone so
      // the caller still gets a readable response.
      try {
        const cloned = response.clone();
        const body = await cloned.json().catch(() => ({ }));
        if (isLegalWall(body)) {
          this.config.onLegalAcceptanceRequired?.(body.stale);
        }
      } catch { /* body already consumed or not JSON — ignore */ }
    }

    return response;
  }

  /** Sends the request; a 401 renews the session and replays it once. A 401 that survives that has already fired `onAuthError`. */
  private async fetchAuthed(endpoint: string, options: RequestInit): Promise<Response> {
    if (!this.isInitialized) {
      throw new Error('ApiClient not initialized. Call initialize() first.');
    }

    let response = await this.send(endpoint, options);
    if (response.status === 401 && this.config.refreshAuth && (await this.refreshOnce(this.config.refreshAuth))) {
      response = await this.send(endpoint, options);
    }
    if (response.status === 401) {
      this.config.onAuthError?.();
    }
    const writes = writesOf(response);
    if (writes !== null) this.config.onWrites?.(writes);
    return response;
  }

  private send(endpoint: string, options: RequestInit): Promise<Response> {
    // Don't set Content-Type for FormData
    const isFormData = options.body instanceof FormData;
    return fetch(`${this.config.baseURL}${endpoint}`, {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
        ...this.identityHeaders(),
      },
    });
  }

  private refreshOnce(refreshAuth: () => Promise<boolean>): Promise<boolean> {
    this.refreshing ??= refreshAuth()
      .catch(() => false)
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  // Helper methods for common HTTP methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, init?: Pick<RequestInit, 'signal'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...init,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  }
}
